import React, { useState, useEffect } from 'react'
import axios from 'axios'
import SecureLS from 'secure-ls'
import { API_CONFIG } from '../../config/api'

const ls = new SecureLS({ encodingType: 'aes' })

/**
 * FamilyMemberEditAdvanced - Comprehensive profile management with platform-specific features
 *
 * Design based on family-member-edit-advanced.svg
 * Features:
 * - Two-column layout for better organization
 * - Basic information section
 * - Platform access & permissions (toggle switches)
 * - Story & memory statistics dashboard
 * - Family tree connections
 * - Privacy & legacy settings
 */
function FamilyMemberEditAdvanced({ memberData, familyId, stats, onSave, onCancel, onBasic, onDelete, onBack }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    relationship: 'member',
    date_of_birth: '',
    email: '',
    phone: '',
    bio: '',
    // Permissions
    can_create_stories: true,
    can_upload_photos: true,
    can_view_tree: true,
    can_create_capsules: false,
    // Family connections
    parents: [],
    spouse: null,
    children: [],
    // Privacy settings
    profile_visibility: 'family_only',
    legacy_mode_configured: false
  })

  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)

  // Profile photo state
  const [profilePhoto, setProfilePhoto] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [photoUploadSuccess, setPhotoUploadSuccess] = useState(false)

  // Initialize form data from memberData
  useEffect(() => {
    if (memberData) {
      setFormData({
        first_name: memberData.first_name || '',
        last_name: memberData.last_name || '',
        relationship: memberData.role || 'member',
        date_of_birth: memberData.date_of_birth || '',
        email: memberData.email || '',
        phone: memberData.phone || '',
        bio: memberData.bio || '',
        can_create_stories: memberData.can_create_stories !== false,
        can_upload_photos: memberData.can_upload_photos !== false,
        can_view_tree: memberData.can_view_tree !== false,
        can_create_capsules: memberData.can_create_capsules || false,
        parents: memberData.parents || [],
        spouse: memberData.spouse || null,
        children: memberData.children || [],
        profile_visibility: memberData.profile_visibility || 'family_only',
        legacy_mode_configured: memberData.legacy_mode_configured || false
      })

      // Initialize profile photo
      setProfilePhoto(memberData.profile_photo_url || null)
    }
  }, [memberData])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleToggle = (fieldName) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }))
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Get file extension
      const fileName = file.name.toLowerCase()
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'))

      // Validate file type - accept images and HEIC/HEIF formats
      const isImage = file.type.startsWith('image/')
      const isHEIC = ['.heic', '.heif'].includes(fileExtension)

      if (!isImage && !isHEIC) {
        setError('Please select an image file (JPG, PNG, HEIC, etc.)')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB')
        return
      }

      setSelectedFile(file)

      // Create a preview URL
      const reader = new FileReader()
      reader.onload = (e) => {
        setProfilePhoto(e.target.result)
      }
      reader.readAsDataURL(file)

      // Upload immediately
      uploadPhoto(file)
    }
  }

  const uploadPhoto = async (file) => {
    setIsUploadingPhoto(true)
    setPhotoUploadSuccess(false)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const token = ls.get('authToken')
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/api/families/${familyId}/members/${memberData.id}/profile-photo/`,
        formData,
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      )

      if (response.data.success) {
        setProfilePhoto(response.data.profile_photo_url)
        setPhotoUploadSuccess(true)
        // Hide success message after 3 seconds
        setTimeout(() => setPhotoUploadSuccess(false), 3000)
      } else {
        setError(response.data.error || 'Failed to upload photo')
        setProfilePhoto(memberData.profile_photo_url || null)
      }
    } catch (err) {
      console.error('Error uploading photo:', err)
      setError('Failed to upload photo')
      setProfilePhoto(memberData.profile_photo_url || null)
    } finally {
      setIsUploadingPhoto(false)
      setSelectedFile(null)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validate required fields
    if (!formData.first_name.trim()) {
      setError('First name is required')
      return
    }

    if (!formData.last_name.trim()) {
      setError('Last name is required')
      return
    }

    setIsSaving(true)

    try {
      const result = await onSave(formData)

      if (!result.success) {
        setError(result.error || 'Failed to save changes')
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setIsSaving(false)
    }
  }

  const initials = (formData.first_name?.[0] || '') + (formData.last_name?.[0] || '')

  // Toggle Switch Component
  const ToggleSwitch = ({ enabled, onToggle, label }) => (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-[#333333]">{label}</span>
      <button
        type="button"
        onClick={onToggle}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          enabled ? 'bg-[#4CAF50]' : 'bg-[#CCCCCC]'
        }`}
      >
        <div
          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
            enabled ? 'translate-x-7' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )

  // Stat Box Component
  const StatBox = ({ icon, label, value }) => (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 bg-[#E8F5E9] rounded-full flex items-center justify-center">
        <span className="text-sm">{icon}</span>
      </div>
      <div>
        <p className="text-xs text-[#666666]">{label}</p>
        <p className="text-lg font-bold text-[#333333]">{value}</p>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#F5F5DC]">
      {/* Header */}
      <div className="bg-[#556B2F] text-white py-5 px-10">
        <h1 className="text-2xl font-bold">One Generation to Next</h1>
      </div>

      {/* Breadcrumb */}
      <div className="px-10 py-4 flex items-center gap-2">
        <button onClick={onBack} className="text-[#E65100] text-sm hover:underline">
          ← Family Members
        </button>
        <span className="text-[#666666] text-sm">/ Edit Profile</span>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-10 pb-12">
        <div className="bg-[#F2F2F2] rounded-xl border border-[#CCCCCC] shadow-md p-8">

          {/* Page Title */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#333333] mb-2">Edit Family Member Profile</h2>
            <p className="text-base text-[#666666]">Manage all information and settings for this family member</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

              {/* LEFT COLUMN - Basic Information */}
              <div className="bg-white rounded-lg border border-[#CCCCCC] p-6">
                <h3 className="text-base font-bold text-[#556B2F] mb-6">Basic Information</h3>

                <div className="flex gap-4 mb-6">
                  {/* Profile Picture */}
                  <div className="flex-shrink-0">
                    {profilePhoto ? (
                      <div className="w-24 h-24 rounded-full overflow-hidden border-[3px] border-[#6B8E23] mb-3">
                        <img
                          src={profilePhoto.startsWith('/') ? `${API_CONFIG.BASE_URL}${profilePhoto}` : profilePhoto}
                          alt={`${formData.first_name} ${formData.last_name} profile`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-[#C8E6C9] border-[3px] border-[#6B8E23] flex items-center justify-center mb-3">
                        <span className="text-3xl font-bold text-[#333333]">{initials}</span>
                      </div>
                    )}

                    <input
                      type="file"
                      id="profile-photo-input-advanced"
                      accept="image/*,.heic,.heif"
                      onChange={handleFileSelect}
                      className="hidden"
                    />

                    <div>
                      <button
                        type="button"
                        onClick={() => document.getElementById('profile-photo-input-advanced').click()}
                        disabled={isUploadingPhoto}
                        className="w-24 px-3 py-2 bg-[#6B8E23] text-white text-xs font-medium rounded-md hover:bg-[#556B2F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploadingPhoto ? 'Uploading...' : photoUploadSuccess ? '✓ Saved!' : 'Change'}
                      </button>
                      <p className="text-xs text-[#666666] mt-1 text-center">Auto-saves</p>
                    </div>
                  </div>

                  {/* Name Fields */}
                  <div className="flex-1 space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-[#333333] mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        value={`${formData.first_name} ${formData.last_name}`}
                        onChange={(e) => {
                          const names = e.target.value.split(' ')
                          setFormData(prev => ({
                            ...prev,
                            first_name: names[0] || '',
                            last_name: names.slice(1).join(' ') || ''
                          }))
                        }}
                        required
                        className="w-full px-3 py-2 text-sm bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                        style={{ height: '40px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#333333] mb-2">Relationship *</label>
                      <select
                        name="relationship"
                        value={formData.relationship}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 text-sm bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                        style={{ height: '40px' }}
                      >
                        <option value="member">Family member</option>
                        <option value="parent">Parent</option>
                        <option value="child">Child</option>
                        <option value="sibling">Sibling</option>
                        <option value="spouse">Spouse</option>
                        <option value="grandparent">Grandparent</option>
                        <option value="grandchild">Grandchild</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-[#333333] mb-2">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                      style={{ height: '40px' }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-[#333333] mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                        style={{ height: '40px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#333333] mb-2">Birth Date</label>
                      <input
                        type="date"
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                        style={{ height: '40px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-[#333333] mb-2">Short Biography</label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-3 py-2 text-xs text-[#333333] bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F] resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN - Platform Access & Stats */}
              <div className="space-y-6">
                {/* Platform Access & Permissions */}
                <div className="bg-white rounded-lg border border-[#CCCCCC] p-6">
                  <h3 className="text-base font-bold text-[#556B2F] mb-4">Platform Access & Permissions</h3>

                  <div className="space-y-2 mb-4">
                    <ToggleSwitch
                      enabled={formData.can_create_stories}
                      onToggle={() => handleToggle('can_create_stories')}
                      label="Can create and share stories"
                    />
                    <ToggleSwitch
                      enabled={formData.can_upload_photos}
                      onToggle={() => handleToggle('can_upload_photos')}
                      label="Can upload photos and videos"
                    />
                    <ToggleSwitch
                      enabled={formData.can_view_tree}
                      onToggle={() => handleToggle('can_view_tree')}
                      label="Can view family tree"
                    />
                    <ToggleSwitch
                      enabled={formData.can_create_capsules}
                      onToggle={() => handleToggle('can_create_capsules')}
                      label="Can create time capsules"
                    />
                  </div>

                  <p className="text-xs text-[#666666]">Control what this member can do on the platform</p>
                </div>

                {/* Story & Memory Statistics */}
                <div className="bg-white rounded-lg border border-[#CCCCCC] p-6">
                  <h3 className="text-base font-bold text-[#556B2F] mb-4">Story & Memory Statistics</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <StatBox icon="📖" label="Stories Shared" value={stats?.stories_shared || 0} />
                    <StatBox icon="⏰" label="Time Capsules" value={stats?.time_capsules || 0} />
                    <StatBox icon="📷" label="Photos Uploaded" value={stats?.photos_uploaded || 0} />
                    <StatBox icon="🍳" label="Recipes Added" value={stats?.recipes_added || 0} />
                    <StatBox icon="🎙️" label="Voice Recordings" value={stats?.voice_recordings || 0} />
                    <StatBox icon="💡" label="Life Lessons" value={stats?.life_lessons || 0} />
                  </div>
                </div>
              </div>
            </div>

            {/* FULL WIDTH SECTIONS */}

            {/* Family Tree Connections */}
            <div className="bg-white rounded-lg border border-[#CCCCCC] p-6 mb-6">
              <h3 className="text-base font-bold text-[#556B2F] mb-2">Family Tree Connections</h3>
              <p className="text-xs text-[#666666] mb-4">Define how this member is connected in the family tree</p>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-2">Parents</label>
                  <select
                    className="w-full px-3 py-2 text-sm bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                    style={{ height: '40px' }}
                  >
                    <option>Select parents... ▼</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-2">Spouse/Partner</label>
                  <select
                    className="w-full px-3 py-2 text-sm bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                    style={{ height: '40px' }}
                  >
                    <option>Select spouse... ▼</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-2">Children</label>
                  <select
                    className="w-full px-3 py-2 text-sm bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                    style={{ height: '40px' }}
                  >
                    <option>Select children... ▼</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Privacy & Legacy Settings */}
            <div className="bg-white rounded-lg border border-[#CCCCCC] p-6 mb-6">
              <h3 className="text-base font-bold text-[#556B2F] mb-2">Privacy & Legacy Settings</h3>
              <p className="text-xs text-[#666666] mb-4">Control who can see their content and how their legacy is preserved</p>

              <div className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-2">Profile Visibility</label>
                  <select
                    name="profile_visibility"
                    value={formData.profile_visibility}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                    style={{ height: '40px' }}
                  >
                    <option value="family_only">Family Only</option>
                    <option value="extended_family">Extended Family</option>
                    <option value="public">Public</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-2">Legacy Mode Status</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.legacy_mode_configured ? 'Configured' : 'Not configured'}
                      readOnly
                      className="flex-1 px-3 py-2 text-sm text-[#666666] bg-white border border-[#CCCCCC] rounded-md"
                      style={{ height: '40px' }}
                    />
                    <button
                      type="button"
                      className="px-4 py-2 bg-[#E65100] text-white text-xs font-medium rounded-md hover:bg-[#D84315] transition-colors"
                    >
                      Configure
                    </button>
                  </div>
                </div>

                <div className="text-xs">
                  <button type="button" className="text-[#E65100] hover:underline">
                    Learn about Legacy Mode →
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4">
              <div className="flex gap-4 items-center">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-6 py-3 bg-[#F2F2F2] text-[#333333] text-sm font-medium border border-[#CCCCCC] rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <span className="text-xs text-[#666666]">* Required fields</span>
              </div>

              <div className="flex gap-4 items-center">
                <button
                  type="button"
                  onClick={onDelete}
                  className="text-[#E65100] text-sm hover:underline"
                >
                  Delete This Member
                </button>

                {onBasic && (
                  <button
                    type="button"
                    onClick={onBasic}
                    className="text-[#556B2F] text-sm hover:underline"
                  >
                    ← Basic Mode
                  </button>
                )}

                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-3 bg-[#556B2F] text-white text-sm font-medium rounded-lg hover:bg-[#4A5D23] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default FamilyMemberEditAdvanced
